import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { useConfig } from "@/contexts/ConfigContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, X, ShieldCheck, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const StatusPill = ({ status, t }) => {
  const tone = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-rose-50 text-rose-700 border-rose-200",
  }[status];
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${tone}`}>{t(status)}</span>;
};

const Approvals = () => {
  const { t } = useLang();
  const { can } = useAuth();
  const cfg = useConfig();
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    const res = await api.get("/stage-requests");
    setItems(res.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, decision) => {
    try {
      await api.post(`/stage-requests/${id}/decide`, { decision });
      toast.success(decision === "approve" ? t("approved_stage_applied") : t("rejected"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const pending = items.filter((i) => i.status === "pending");
  const history = items.filter((i) => i.status !== "pending");

  const DecisionCell = ({ r }) => {
    if (r.status !== "pending") {
      return (
        <div className="text-right">
          <StatusPill status={r.status} t={t} />
          <div className="text-[11px] text-slate-400 mt-1">{t("decided_by")}: {r.decided_by_name}</div>
        </div>
      );
    }
    if (!can("deals:approve")) return <StatusPill status="pending" t={t} />;
    return (
      <div className="flex gap-2">
        <Button size="sm" onClick={() => decide(r.id, "approve")} data-testid={`approve-button-${r.id}`} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5">
          <Check className="w-3.5 h-3.5" /> {t("approve")}
        </Button>
        <Button size="sm" variant="outline" onClick={() => decide(r.id, "reject")} data-testid={`reject-button-${r.id}`} className="border-rose-200 text-rose-600 hover:bg-rose-50 gap-1.5">
          <X className="w-3.5 h-3.5" /> {t("reject")}
        </Button>
      </div>
    );
  };

  const Row = ({ r }) => (
    <div className="p-4 flex flex-wrap items-center gap-3" data-testid={`approval-row-${r.id}`}>
      <div className="flex-1 min-w-[220px]">
        <div className="text-sm font-semibold text-slate-900">{r.deal_title}</div>
        <div className="text-xs text-slate-500 mt-1">
          {t("requested_by")}: {r.requested_by_name} · {new Date(r.created_at).toLocaleString()}
        </div>
      </div>
      <div className="flex items-center gap-2 text-xs">
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-semibold border ${cfg.stageStyle(r.from_stage).bg} ${cfg.stageStyle(r.from_stage).border} ${cfg.stageStyle(r.from_stage).text}`}>
          {cfg.stageLabel(r.from_stage)}
        </span>
        <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-semibold border ${cfg.stageStyle(r.to_stage).bg} ${cfg.stageStyle(r.to_stage).border} ${cfg.stageStyle(r.to_stage).text}`}>
          {cfg.stageLabel(r.to_stage)}
        </span>
      </div>
      <DecisionCell r={r} />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="approvals-view">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("approvals")}</h2>
        <p className="text-sm text-slate-500 mt-1">{pending.length} {t("pending").toLowerCase()}</p>
      </div>

      {pending.length === 0 ? (
        <Card className="p-12 text-center text-slate-400 border-dashed">
          <ShieldCheck className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-sm">{t("no_pending_approvals")}</p>
        </Card>
      ) : (
        <Card className="border-slate-200 shadow-sm divide-y divide-slate-100">
          {pending.map((r) => <Row key={r.id} r={r} />)}
        </Card>
      )}

      {history.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-base font-semibold text-slate-900">{t("history")}</h3>
          <Card className="border-slate-200 shadow-sm divide-y divide-slate-100">
            {history.map((r) => <Row key={r.id} r={r} />)}
          </Card>
        </div>
      )}
    </div>
  );
};

export default Approvals;
